import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Bell, LogOut, LayoutDashboard, BadgeCheck, CalendarCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { getSessionInfo, clearSession, SESSION_EVENT } from "../lib/register";

export default function TopNav({ variant = "public" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [openNotif, setOpenNotif] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  // The VERIFIED ATTENDEE session — entirely separate from `user`, which is an
  // admin account. An attendee has no account, so this is deliberately presented
  // as "verified", never "logged in".
  const [verified, setVerified] = useState(getSessionInfo());
  const [openVerified, setOpenVerified] = useState(false);

  useEffect(() => {
    const sync = () => setVerified(getSessionInfo());
    sync();
    window.addEventListener(SESSION_EVENT, sync);
    window.addEventListener("focus", sync);   // catch expiry after a long idle
    return () => {
      window.removeEventListener(SESSION_EVENT, sync);
      window.removeEventListener("focus", sync);
    };
  }, [loc.pathname]);

  useEffect(() => {
    if (!user) return;
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  }, [user, loc.pathname]);

  // Pending-approvals badge on the Requests nav item (admin only): quiet at
  // zero, glanceable when work is waiting.
  const [pendingReqs, setPendingReqs] = useState(0);
  useEffect(() => {
    if (!user || variant !== "admin") return;
    api.get("/admin/requests/count").then((r) => setPendingReqs(r.data?.count || 0)).catch(() => {});
  }, [user, variant, loc.pathname]);

  const unread = notifs.filter((n) => !n.read).length;

  const publicLinks = [{ to: "/events", label: "Events" }];
  const adminLinks = [
    { to: "/admin/community", label: "Community" },
    { to: "/admin/events", label: "Events" },
    { to: "/admin/sessions", label: "Requests" },
    { to: "/admin/programs", label: "Programs" },
    { to: "/admin/integrations", label: "Integrations" },
  ];
  const links = variant === "admin" ? adminLinks : publicLinks;

  return (
    <header className="sticky top-0 z-40 glass-nav" data-testid="top-nav">
      <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="brand-logo">
          <img src="/venturedock-logo.webp" alt="Venture Dock" className="h-7 md:h-8 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-lg text-sm transition-all ${isActive ? "text-foreground font-medium bg-white/60" : "text-muted-foreground hover:text-foreground hover:bg-white/40"}`
              }
            >
              {l.label}
              {l.to === "/admin/sessions" && pendingReqs > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-medium text-white align-middle"
                      style={{ background: "hsl(var(--vd-coral))" }} data-testid="requests-badge">
                  {pendingReqs}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user && (
            <div className="relative">
              <button
                onClick={() => setOpenNotif((v) => !v)}
                data-testid="notifications-btn"
                className="h-9 w-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-white/40 transition relative"
              >
                <Bell size={16} />
                {unread > 0 && (
                  <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full" style={{ background: "hsl(var(--vd-coral))" }} />
                )}
              </button>
              {openNotif && (
                <div className="absolute right-0 mt-2 w-80 glass-panel p-2 z-50" data-testid="notifications-panel">
                  <div className="px-3 py-2 flex items-center justify-between">
                    <span className="overline">Notifications</span>
                    <span className="text-xs text-muted-foreground">{unread} unread</span>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifs.length === 0 ? (
                      <div className="text-sm text-muted-foreground p-4 text-center">All caught up</div>
                    ) : (
                      notifs.map((n) => (
                        <button
                          key={n.id}
                          onClick={async () => {
                            await api.post(`/notifications/${n.id}/read`).catch(() => {});
                            setNotifs((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
                            if (n.link) nav(n.link);
                            setOpenNotif(false);
                          }}
                          className="w-full text-left p-3 rounded-xl hover:bg-white/60 transition"
                          data-testid={`notif-${n.id}`}
                        >
                          <div className="flex items-start gap-2">
                            {!n.read && <div className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: "hsl(var(--vd-coral))" }} />}
                            <div className="flex-1">
                              <div className="text-sm font-medium">{n.title}</div>
                              <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {!user && verified && (
            <div className="relative">
              <button onClick={() => setOpenVerified((v) => !v)}
                      className="h-9 px-3 rounded-lg flex items-center gap-2 hover:bg-white/60 transition"
                      data-testid="verified-menu-btn">
                <div className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-medium text-white"
                     style={{ background: "hsl(var(--vd-green))" }}>
                  {verified.email[0]?.toUpperCase()}
                </div>
                <span className="text-sm hidden sm:inline max-w-[160px] truncate">{verified.email}</span>
              </button>
              {openVerified && (
                <div className="absolute right-0 mt-2 w-72 glass-panel p-1.5 z-50" data-testid="verified-menu">
                  <div className="px-3 py-2 border-b border-border/40">
                    <div className="flex items-center gap-1.5 text-sm font-medium">
                      <BadgeCheck size={14} style={{ color: "hsl(var(--vd-green))" }} />
                      {verified.scope === "signin" ? "Signed in" : "Email verified"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 break-all">{verified.email}</div>
                    <div className="flex gap-1.5 mt-2">
                      {verified.isMember && <span className="chip chip-purple text-[10px]">Member</span>}
                      <span className="chip chip-cream-2 text-[10px] font-mono">
                        {verified.daysLeft >= 1
                          ? `${verified.daysLeft}d left`
                          : verified.minutesLeft >= 60
                            ? `${Math.floor(verified.minutesLeft / 60)}h ${verified.minutesLeft % 60}m left`
                            : `${verified.minutesLeft}m left`}
                      </span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-2 leading-snug">
                      {verified.scope === "signin"
                        ? "You're signed in on this device. It is not an account — nothing is stored here but this session."
                        : "You can register without re-entering a code while this lasts. It is not an account — nothing is stored on this device but this."}
                    </div>
                  </div>
                  <Link to="/my-events" onClick={() => setOpenVerified(false)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-white/60"
                        data-testid="verified-my-events">
                    <CalendarCheck size={14} /> My events
                  </Link>
                  <button onClick={() => { clearSession(); setOpenVerified(false); nav("/events"); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-white/60"
                          style={{ color: "hsl(var(--vd-coral))" }} data-testid="verified-signout">
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
              )}
            </div>
          )}

          {!user ? (
            !verified && variant !== "admin" && (
              <Link to="/my-events" className="btn-ghost text-sm" data-testid="nav-signin">
                Sign in
              </Link>
            )
          ) : (
            <div className="relative">
              <button onClick={() => setOpenMenu((v) => !v)} className="h-9 px-3 rounded-lg flex items-center gap-2 hover:bg-white/60 transition" data-testid="user-menu-btn">
                <div className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-medium text-white" style={{ background: "hsl(var(--vd-green))" }}>
                  {user.first_name?.[0]}{user.last_name?.[0]}
                </div>
                <span className="text-sm hidden sm:inline">{user.first_name}</span>
              </button>
              {openMenu && (
                <div className="absolute right-0 mt-2 w-56 glass-panel p-1.5 z-50" data-testid="user-menu">
                  <div className="px-3 py-2 border-b border-border/40">
                    <div className="text-sm font-medium">{user.first_name} {user.last_name}</div>
                    <div className="text-xs text-muted-foreground">{user.email}</div>
                    <div className="mt-1 chip chip-green text-[10px]">{user.role}</div>
                  </div>
                  {user.role === "admin" && (
                    <Link to="/admin/community" onClick={() => setOpenMenu(false)} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-white/60" data-testid="menu-admin">
                      <LayoutDashboard size={14} /> Admin
                    </Link>
                  )}
                  <button onClick={() => { logout(); nav("/"); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-white/60" style={{ color: "hsl(var(--vd-coral))" }} data-testid="logout-btn">
                    <LogOut size={14} /> Log out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
